/**
 * Admin tab — Collect The Flags (iter 95).
 *
 * Shows:
 *  • Overview counters (queue, active rooms, disputes, collections)
 *  • Per-platform queue list with kick action
 *  • Rooms table with status filter + close action
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Users, UserX, FlagOff, RefreshCw, Bot, Camera } from "lucide-react";
import ProtectedImage from "../components/ProtectedImage";

const PLATFORMS = ["mobile", "console"];
const STATUSES = ["", "matched", "attested", "reported", "closed", "disputed"];

export default function AdminCtfTab() {
  const [platform, setPlatform] = useState("mobile");
  const [overview, setOverview] = useState(null);
  const [queue, setQueue] = useState([]);
  const [rooms, setRooms] = useState(null);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, q, rm] = await Promise.all([
        api.get("/ctf/admin/overview"),
        api.get(`/ctf/admin/queue?platform=${platform}`),
        api.get(`/ctf/admin/rooms?platform=${platform}${status ? `&status=${status}` : ""}&limit=50`),
      ]);
      setOverview(ov.data);
      setQueue(q.data.queue || []);
      setRooms(rm.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [platform, status]);

  useEffect(() => { load(); }, [load]);

  const kickFromQueue = async (uid) => {
    if (!window.confirm("Remove this player from the queue?")) return;
    try {
      await api.post(`/ctf/admin/queue/${uid}/kick`);
      toast.success("Removed from queue.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const closeRoom = async (rid) => {
    if (!window.confirm("Force-close this room?")) return;
    try {
      await api.post(`/ctf/admin/rooms/${rid}/close`);
      toast.success("Room closed.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [aiBusy, setAiBusy] = useState(null);
  const aiCheck = async (rid) => {
    setAiBusy(rid);
    try {
      const { data } = await api.post(`/ctf/admin/rooms/${rid}/ai-check`);
      const v = data.verdict || {};
      toast.message(`AI: ${v.home_score != null ? `${v.home_score}–${v.away_score}` : "no verdict"} · conf ${(v.confidence ?? 0).toFixed?.(2) ?? v.confidence}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiBusy(null); }
  };

  if (loading || !overview) {
    return (
      <div className="p-8 text-center" data-testid="admin-ctf-loading">
        <Loader2 className="w-5 h-5 animate-spin mx-auto text-[#60A5FA]" />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4" data-testid="admin-ctf-tab">
      <div className="flex items-center justify-between">
        <div className="text-[12px] font-mono uppercase tracking-[0.3em] text-[#60A5FA]">
          Collect The Flags · Ops
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="admin-ctf-refresh"
          className="inline-flex items-center gap-1 text-[10px] font-mono text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="admin-ctf-counters">
        <StatTile label="Queue" value={overview.counters.queue_size} />
        <StatTile label="Matched" value={overview.counters.matched_rooms} />
        <StatTile label="Attested" value={overview.counters.attested_rooms} />
        <StatTile label="Reported" value={overview.counters.reported_rooms} />
        <StatTile label="Closed" value={overview.counters.closed_rooms} />
        <StatTile label="Disputed" value={overview.counters.disputed_rooms} accent="#EF4444" />
        <StatTile label="Collections" value={overview.counters.total_collections} />
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[10px] font-mono uppercase text-[#71717A]">Platform:</span>
        {PLATFORMS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPlatform(p)}
            data-testid={`admin-ctf-platform-${p}`}
            className={`px-2 py-1 rounded text-[10px] font-mono uppercase tracking-widest border transition ${
              platform === p
                ? "border-[#60A5FA] text-[#60A5FA] bg-[#60A5FA]/10"
                : "border-[#E4E4E7] text-[#71717A]"
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      <Section title={`Queue (${queue.length})`} icon={<Users className="w-3 h-3 text-[#60A5FA]" />}>
        {queue.length === 0 ? (
          <div className="text-[11px] text-[#71717A] py-4 text-center">No players waiting.</div>
        ) : (
          <table className="w-full text-[11px] font-mono" data-testid="admin-ctf-queue-table">
            <thead>
              <tr className="text-[9px] uppercase tracking-widest text-[#71717A]">
                <th className="text-left p-1">Player</th>
                <th className="text-left p-1">Squad</th>
                <th className="text-left p-1">Wants</th>
                <th className="text-left p-1">Joined</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {queue.map((q) => (
                <tr key={q.id} className="border-t border-[#E4E4E7] dark:border-[#2A2A30]">
                  <td className="p-1.5">@{q.user?.username || q.user_id.slice(0, 8)}</td>
                  <td className="p-1.5">{q.squad_nation}</td>
                  <td className="p-1.5">{(q.want_list || []).join(", ")}</td>
                  <td className="p-1.5 text-[#71717A]">{(q.joined_at || "").slice(11, 16)}</td>
                  <td className="p-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => kickFromQueue(q.user_id)}
                      data-testid={`admin-ctf-kick-${q.user_id}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] uppercase text-[#EF4444] border border-[#EF4444]/30 hover:bg-[#EF4444]/10"
                    >
                      <UserX className="w-3 h-3" /> Kick
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title={`Rooms (${rooms?.total || 0})`} icon={<FlagOff className="w-3 h-3 text-[#60A5FA]" />}>
        <div className="flex items-center gap-1 mb-2 flex-wrap">
          <span className="text-[9px] uppercase font-mono text-[#71717A]">Status:</span>
          {STATUSES.map((s) => (
            <button
              key={s || "all"}
              type="button"
              onClick={() => setStatus(s)}
              data-testid={`admin-ctf-status-${s || "all"}`}
              className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase border transition ${
                status === s
                  ? "border-[#60A5FA] text-[#60A5FA]"
                  : "border-transparent text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
              }`}
            >
              {s || "all"}
            </button>
          ))}
        </div>
        {(rooms?.rooms || []).length === 0 ? (
          <div className="text-[11px] text-[#71717A] py-4 text-center">No rooms.</div>
        ) : (
          <div className="space-y-2" data-testid="admin-ctf-rooms-list">
            {rooms.rooms.map((r) => (
              <RoomCard
                key={r.id}
                r={r}
                aiBusy={aiBusy === r.id}
                onAiCheck={() => aiCheck(r.id)}
                onClose={() => closeRoom(r.id)}
              />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function RoomCard({ r, aiBusy, onAiCheck, onClose }) {
  const v = r.ai_verdict;
  const hasShots = r.p1_result_screenshot || r.p2_result_screenshot;
  return (
    <div className="rounded-lg border border-[#E4E4E7] dark:border-[#2A2A30] p-2.5" data-testid={`admin-ctf-room-${r.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] font-mono">
          <span className="text-[#71717A]">{r.id.slice(0, 8)}… ·</span>{" "}
          <span className="font-bold">@{r.p1?.username}</span> vs <span className="font-bold">@{r.p2?.username}</span>
          <span className="text-[#71717A]"> · Tier {r.tier ?? "—"}</span>
        </div>
        <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-mono uppercase"
              style={{ background: statusColor(r.status) + "20", color: statusColor(r.status) }}>
          {r.status}
        </span>
      </div>

      {/* Submitted results */}
      <div className="grid grid-cols-2 gap-2 mt-2">
        <SubmissionView label={`@${r.p1?.username}`} score={r.p1_score} opp={r.p1_opp_score_reported}
                        pct={r.p1_completion_pct} reported={r.p1_reported} shot={r.p1_result_screenshot} />
        <SubmissionView label={`@${r.p2?.username}`} score={r.p2_score} opp={r.p2_opp_score_reported}
                        pct={r.p2_completion_pct} reported={r.p2_reported} shot={r.p2_result_screenshot} />
      </div>

      {/* AI verdict */}
      {v && (
        <div className="mt-2 text-[10px] font-mono rounded bg-[#60A5FA]/10 border border-[#60A5FA]/30 px-2 py-1 inline-flex items-center gap-1.5">
          <Bot className="w-3 h-3 text-[#60A5FA]" />
          AI: {v.home_score != null ? `${v.home_score}–${v.away_score}` : "no verdict"} · conf {(v.confidence ?? 0)}
          {v.reasoning ? <span className="text-[#71717A]"> · {String(v.reasoning).slice(0, 80)}</span> : null}
        </div>
      )}

      <div className="flex items-center justify-end gap-1.5 mt-2">
        <button type="button" onClick={onAiCheck} disabled={aiBusy || !hasShots}
                title={hasShots ? "Compare both screenshots with AI" : "No screenshots submitted"}
                data-testid={`admin-ctf-ai-${r.id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] uppercase font-mono text-[#60A5FA] border border-[#60A5FA]/40 hover:bg-[#60A5FA]/10 disabled:opacity-40">
          {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bot className="w-3 h-3" />} AI verify
        </button>
        {r.status !== "closed" && (
          <button type="button" onClick={onClose}
                  data-testid={`admin-ctf-close-${r.id}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] uppercase font-mono text-[#EF4444] border border-[#EF4444]/30 hover:bg-[#EF4444]/10">
            Close
          </button>
        )}
      </div>
    </div>
  );
}

function SubmissionView({ label, score, opp, pct, reported, shot }) {
  return (
    <div className="rounded border border-[#E4E4E7] dark:border-[#2A2A30] p-1.5">
      <div className="text-[9px] font-mono uppercase tracking-widest text-[#71717A] truncate">{label}</div>
      {reported ? (
        <>
          <div className="text-[12px] font-mono font-bold tabular-nums">{score ?? "?"}–{opp ?? "?"}
            <span className="text-[9px] text-[#71717A] font-normal"> · {pct ?? "?"}%</span></div>
          {shot ? (
            <ProtectedImage path={shot} className="mt-1 max-h-28 rounded border border-[#2A2A30]" />
          ) : (
            <div className="mt-1 text-[9px] font-mono text-[#71717A] inline-flex items-center gap-1"><Camera className="w-3 h-3" /> no screenshot</div>
          )}
        </>
      ) : (
        <div className="text-[10px] font-mono text-[#71717A] mt-0.5">not reported yet</div>
      )}
    </div>
  );
}

function StatTile({ label, value, accent }) {
  return (
    <div
      className="rounded-lg border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#1A1A1E] p-2.5"
      data-testid={`admin-ctf-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-[9px] uppercase tracking-widest text-[#71717A]">{label}</div>
      <div
        className="text-2xl font-extrabold mt-0.5"
        style={{ color: accent || "#09090B" }}
      >
        {value}
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="rounded-xl border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#1A1A1E] p-3">
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.25em] text-[#71717A] mb-2">
        {icon} {title}
      </div>
      {children}
    </div>
  );
}

function statusColor(s) {
  switch (s) {
    case "matched": return "#60A5FA";
    case "attested": return "#FFD700";
    case "reported": return "#22D3EE";
    case "closed": return "#71717A";
    case "disputed": return "#EF4444";
    default: return "#71717A";
  }
}
